import { isTerminalSessionContextError } from "@/lib/query/terminal-session-error";

const NETWORK_ERROR_MESSAGE_PATTERN =
  /(load failed|failed to fetch|network ?error|network request failed|connection (reset|closed|aborted)|econnreset)/i;
const ABORT_MESSAGE_PATTERN = /\bAbortError\b|\bsignal (?:is )?aborted\b|\boperation was aborted\b/i;
const SERVER_STATUS_IN_MESSAGE_PATTERN = /\b5\d{2}\b/;

export const MAX_TRANSIENT_NETWORK_RETRIES = 2;

type ErrorLike = {
  message?: unknown;
  name?: unknown;
  code?: unknown;
  status?: unknown;
  statusCode?: unknown;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

function readString(error: unknown, key: "message" | "name" | "code"): string | undefined {
  if (!isObject(error)) return undefined;
  const value = (error as ErrorLike)[key];
  return typeof value === "string" ? value : undefined;
}

function readNumber(error: unknown, keys: Array<"status" | "statusCode" | "code">): number | undefined {
  if (!isObject(error)) return undefined;

  for (const key of keys) {
    const value = (error as ErrorLike)[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }

  return undefined;
}

function extractMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  const message = readString(error, "message");
  if (message) return message;
  return String(error);
}

export function isCancellationError(error: unknown): boolean {
  const name = readString(error, "name");
  if (name === "AbortError") return true;

  const codeString = readString(error, "code");
  if (codeString === "ABORT_ERR") return true;

  const codeNumber = readNumber(error, ["code"]);
  if (codeNumber === 20) return true;

  const status = readNumber(error, ["status", "statusCode"]);
  return status === 0 && ABORT_MESSAGE_PATTERN.test(extractMessage(error));
}

export function isTransientNetworkError(error: unknown): boolean {
  if (isCancellationError(error)) return false;

  const status = readNumber(error, ["status", "statusCode"]);
  if (typeof status === "number" && status >= 400) return false;

  const code = readString(error, "code");
  if (code?.startsWith("PGRST")) return false;

  const message = extractMessage(error);
  if (SERVER_STATUS_IN_MESSAGE_PATTERN.test(message)) return false;

  if (status === 0) return true;

  const name = readString(error, "name");
  const hasBrowserFetchShape =
    error instanceof TypeError || name === "TypeError" || name === "FetchError";

  return hasBrowserFetchShape && NETWORK_ERROR_MESSAGE_PATTERN.test(message);
}

export function transientNetworkRetry(failureCount: number, error: unknown): boolean {
  if (isTerminalSessionContextError(error)) return false;
  return isTransientNetworkError(error) && failureCount < MAX_TRANSIENT_NETWORK_RETRIES;
}

export function transientNetworkRetryDelay(attemptIndex: number): number {
  return attemptIndex === 0 ? 300 : 900;
}
